import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, type UsuarioPublico } from '../stores/authStore';
import { apiPost, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';
import { ErrorMessage } from '../components/ui/ErrorMessage';

interface LoginResponse {
  token: string;
  usuario: UsuarioPublico;
}

export function LoginPage() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const isAuthenticated = useAuthStore((state) => state.token !== null);

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Se já está logado, redireciona
  if (isAuthenticated) {
    navigate('/atendimento', { replace: true });
    return null;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !senha) {
      setError('Preencha email e senha');
      return;
    }

    setLoading(true);
    try {
      const response = await apiPost<LoginResponse>('/api/auth/login', {
        email: email.trim(),
        senha,
      });

      login(response.token, response.usuario);
      navigate('/atendimento', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message || 'Erro ao fazer login');
      } else {
        setError('Erro de conexão. Verifique se o backend está rodando.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-primary-400 mb-2">
            Suporte Nexo
          </h1>
          <p className="text-gray-400 text-sm">
            Entre com suas credenciais para acessar
          </p>
        </div>

        {/* Form card */}
        <form onSubmit={handleSubmit} className="card p-8 space-y-5" noValidate>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <TextInput
            id="email"
            type="email"
            label="Email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            disabled={loading}
            required
          />

          <TextInput
            id="senha"
            type="password"
            label="Senha"
            placeholder="••••••••"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
            disabled={loading}
            required
          />

          <Button
            type="submit"
            variant="primary"
            loading={loading}
            className="w-full"
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>
        </form>

        {/* Footer info */}
        <p className="text-center text-xs text-gray-500 mt-6">
          Sistema interno do Nexo · Acesso restrito
        </p>
      </div>
    </div>
  );
}
